import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface TelegramModule {
  id: string;
  name: string;
  description: string | null;
  type: string;
  config: string | null;
  code: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const TelegramModules: React.FC = () => {
  const [modules, setModules] = useState<TelegramModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState<TelegramModule | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'send_message',
    config: '',
    code: '',
    isActive: true
  });

  useEffect(() => {
    fetchModules();
  }, [filterType]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const params = filterType ? `?type=${filterType}` : '';
      const data = await apiGet<TelegramModule[]>(`/telegram-modules${params}`);
      setModules(data);
    } catch (error) {
      console.error('Ошибка загрузки модулей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (module?: TelegramModule) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        name: module.name,
        description: module.description || '',
        type: module.type,
        config: module.config ? JSON.stringify(JSON.parse(module.config), null, 2) : '',
        code: module.code || '',
        isActive: module.isActive
      });
    } else {
      setEditingModule(null);
      setFormData({
        name: '',
        description: '',
        type: 'send_message',
        config: '',
        code: '',
        isActive: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModule(null);
    setFormData({
      name: '',
      description: '',
      type: 'send_message',
      config: '',
      code: '',
      isActive: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        config: formData.config ? JSON.parse(formData.config) : null,
        code: formData.code || null
      };

      if (editingModule) {
        await apiPut(`/telegram-modules/${editingModule.id}`, payload);
      } else {
        await apiPost('/telegram-modules', payload);
      }

      await fetchModules();
      handleCloseModal();
    } catch (error: any) {
      console.error('Ошибка сохранения модуля:', error);
      alert(error.response?.data?.error || 'Ошибка сохранения модуля');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот модуль?')) {
      return;
    }

    try {
      await apiDelete(`/telegram-modules/${id}`);
      await fetchModules();
    } catch (error: any) {
      console.error('Ошибка удаления модуля:', error);
      alert(error.response?.data?.error || 'Ошибка удаления модуля');
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'send_message': 'Отправить сообщение',
      'check_condition': 'Проверить условие',
      'get_data': 'Получить данные',
      'wait_input': 'Ожидать ввода',
      'execute_code': 'Выполнить код'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">🧩 Модули Telegram бота</h1>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Добавить модуль
        </button>
      </div>

      {/* Фильтр */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Тип модуля:</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Все типы</option>
            <option value="send_message">Отправить сообщение</option>
            <option value="check_condition">Проверить условие</option>
            <option value="get_data">Получить данные</option>
            <option value="wait_input">Ожидать ввода</option>
            <option value="execute_code">Выполнить код</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Описание
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Тип
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {modules.map((module) => (
              <tr key={module.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{module.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500">{module.description || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                    {getTypeLabel(module.type)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    module.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {module.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal(module)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => handleDelete(module.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {modules.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Модули не найдены</p>
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingModule ? 'Редактировать модуль' : 'Создать модуль'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тип модуля *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="send_message">Отправить сообщение</option>
                  <option value="check_condition">Проверить условие</option>
                  <option value="get_data">Получить данные</option>
                  <option value="wait_input">Ожидать ввода</option>
                  <option value="execute_code">Выполнить код</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Конфигурация (JSON)
                </label>
                <textarea
                  value={formData.config}
                  onChange={(e) => setFormData({ ...formData, config: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={5}
                  placeholder='{"text": "Привет!", "parse_mode": "HTML"}'
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Код модуля (опционально)
                </label>
                <textarea
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={8}
                  placeholder="// JavaScript код модуля"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Активен</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramModules;

